import { NavLink, useNavigate } from "react-router-dom";
import { ScrollText, CalendarDays, Link2, MessagesSquare, LogIn, LogOut, Shield, Settings } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const NAV = [
  { to: "/about", label: "nav.about", icon: ScrollText, testid: "nav-about" },
  { to: "/events", label: "nav.events", icon: CalendarDays, testid: "nav-events" },
  { to: "/links", label: "nav.links", icon: Link2, testid: "nav-links" },
  { to: "/chat", label: "nav.chat", icon: MessagesSquare, testid: "nav-chat" },
];

export function TopNav() {
  const { user, logout } = useAuth();
  const { t } = useSettings();
  const navigate = useNavigate();

  return (
    <header className="hidden md:block sticky top-0 z-40 border-b border-zinc-800/60 bg-black/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <NavLink to="/about" data-testid="header-home-link">
          <LogoLockup />
        </NavLink>
        <nav className="flex items-center gap-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-2 px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={16} className={isActive ? "text-crimson-bright" : "text-zinc-500 group-hover:text-silver"} />
                  <span>{t(label)}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <NavLink
                  to="/admin"
                  data-testid="nav-admin"
                  className={({ isActive }) =>
                    `btn-ghost px-3 py-1.5 text-xs tracking-widest uppercase inline-flex items-center gap-2 ${isActive ? "border-crimson text-white" : ""}`
                  }
                >
                  <Settings size={14} /> Admin
                </NavLink>
              )}
              <div className="flex items-center gap-2" data-testid="header-user">
                {user.role === "admin" && <Shield size={14} className="text-crimson-bright" />}
                <span className="text-sm text-silver">{user.username || user.name}</span>
              </div>
              <button
                onClick={async () => { await logout(); navigate("/login"); }}
                className="btn-ghost px-3 py-1.5 text-xs tracking-widest uppercase inline-flex items-center gap-2"
                data-testid="logout-button"
              >
                <LogOut size={14} /> Logout
              </button>
            </>
          ) : (
            <NavLink
              to="/login"
              className="btn-crimson px-4 py-2 text-xs tracking-widest uppercase inline-flex items-center gap-2"
              data-testid="header-login-link"
            >
              <LogIn size={14} /> {t("nav.enter")}
            </NavLink>
          )}
        </div>
      </div>
    </header>
  );
}

export function BottomNav() {
  const { user } = useAuth();
  const { t } = useSettings();
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-zinc-800/60 bg-black/85 backdrop-blur-xl">
      <div className="grid grid-cols-4">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2.5 gap-1 text-[10px] tracking-widest uppercase ${
                isActive ? "text-white" : "text-zinc-500"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={20} className={isActive ? "text-crimson-bright" : "text-zinc-500"} />
                <span className="truncate max-w-full px-1">{t(label)}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
      {!user && (
        <div className="border-t border-zinc-900 bg-black/90 px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] tracking-widest uppercase text-zinc-500">Guest</span>
          <NavLink to="/login" className="text-[10px] tracking-widest uppercase text-crimson-bright" data-testid="mobile-login-link">
            {t("nav.enter")} →
          </NavLink>
        </div>
      )}
    </nav>
  );
}

export function MobileHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="md:hidden sticky top-0 z-30 border-b border-zinc-800/60 bg-black/80 backdrop-blur-xl">
      <div className="px-4 h-14 flex items-center justify-between">
        <NavLink to="/about"><LogoLockup compact /></NavLink>
        {user && (
          <div className="flex items-center gap-3">
            {user.role === "admin" && (
              <NavLink to="/admin" data-testid="nav-admin-mobile" className="text-silver p-1" aria-label="Admin">
                <Settings size={18} />
              </NavLink>
            )}
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-button-mobile"
              className="text-zinc-400 p-1"
              aria-label="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
